"use client";

import { useTranslations } from "next-intl";
import { useEffect, useMemo, useState } from "react";
import { useDateFormatter } from "@/lib/i18n/date";

export interface CycleCountdownProps {
  /** Unix seconds — countdown target. */
  targetUnixSeconds: number;
  /**
   * "neutral" — pre-election (Frame 5462:51594 default).
   *   3-item row: counter (right, RTL) | days remaining (middle) | Jalali date (left).
   *   Per Figma 5462:51502 — no title.
   * "active"  — candidacy + voting active (Frame 5462:51697 yellow border).
   *   Title + days/counter row inside accent-secondary border.
   */
  variant?: "neutral" | "active";
}

interface BreakdownReturn {
  days: number;
  hh: string;
  mm: string;
  ss: string;
}

function breakdown(remainingSeconds: number): BreakdownReturn {
  const days = Math.floor(remainingSeconds / 86400);
  const hours = Math.floor((remainingSeconds % 86400) / 3600);
  const minutes = Math.floor((remainingSeconds % 3600) / 60);
  const seconds = remainingSeconds % 60;
  return {
    days,
    hh: String(hours).padStart(2, "0"),
    mm: String(minutes).padStart(2, "0"),
    ss: String(seconds).padStart(2, "0"),
  };
}

export const CycleCountdown: React.FC<CycleCountdownProps> = ({
  targetUnixSeconds,
  variant = "neutral",
}) => {
  const t = useTranslations("dao");
  const df = useDateFormatter();
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Math.floor(Date.now() / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const remaining = Math.max(0, targetUnixSeconds - now);
  const { days, hh, mm, ss } = breakdown(remaining);
  const counter = `${hh} : ${mm} : ${ss}`;
  const jalaliDay = useMemo(
    () => (targetUnixSeconds ? df.long(targetUnixSeconds) : ""),
    [targetUnixSeconds, df],
  );

  if (variant === "active") {
    return (
      <div
        dir="rtl"
        className="flex w-full flex-col items-center justify-center gap-2.5 rounded-xl border border-accent-secondary px-4 py-3 text-text-primary"
      >
        <p className="text-center text-base font-semibold leading-[1.4]">
          {t("election.cycle.activeBanner")}
        </p>
        <div className="flex w-full items-center justify-center gap-4 px-3 text-accent">
          <bdi
            className="font-secondary text-xl font-semibold leading-[1.4]"
            dir="ltr"
          >
            {counter}
          </bdi>
          <p className="text-sm font-normal leading-[1.6]">
            {t("election.cycle.daysLeftActive", {
              days: days.toLocaleString("fa-IR"),
            })}
          </p>
        </div>
      </div>
    );
  }

  // Neutral — Figma 5462:51502: 3 items, RTL.
  // Right (start): counter HH:MM:SS — Modam SemiBold 20.
  // Middle: "{N} روز باقیمانده" — Peyda Regular 14.
  // Left (end): Jalali date "14تیر 1405" — Modam SemiBold 24.
  return (
    <div
      dir="rtl"
      className="flex w-full items-center justify-between rounded-2xl bg-bg-secondary p-3 text-center text-text-primary"
    >
      <bdi
        className="font-secondary text-xl font-semibold leading-[1.4]"
        dir="ltr"
      >
        {counter}
      </bdi>
      <p className="text-sm font-normal leading-[1.6]">
        {t("election.cycle.daysLeft", { days: days.toLocaleString("fa-IR") })}
      </p>
      <p className="font-secondary text-2xl font-semibold leading-[1.4]">
        {jalaliDay}
      </p>
    </div>
  );
};
